import React, { useState, useEffect } from 'react';
import ProductsManagementHeader from './ProductsManagementHeader';
import ProductsTable from './ProductsTable';
import { Plus } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { CommonButton } from '@/components/ui/common-button';
import { FullScreenLoader, TableBlurLoader } from '@/components/ui/loader';
import AddProductDialog from './addproducts/AddProductDialog';
import EditProductDialog from './editproducts/EditProductDialog';
import DeleteProductDialog from './deleteproducts/DeleteProductDialog';
import productService from '@/services/productService';
import { usePagination } from '@/hooks/usePagination';

const ProductsManagementPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [addProductDialogOpen, setAddProductDialogOpen] = useState(false);
  const [editProductDialogOpen, setEditProductDialogOpen] = useState(false);
  const [deleteProductDialogOpen, setDeleteProductDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductDetails, setSelectedProductDetails] = useState(null);
  const [totalProducts, setTotalProducts] = useState(0);
  
  const { currentPage, pageSize, setPage } = usePagination({ defaultPage: 1, defaultPageSize: 10 });
  
  const { toast } = useToast();

  useEffect(() => {
    fetchProducts();
  }, [currentPage, pageSize]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * pageSize;
      const productData = await productService.getProducts(skip, pageSize, false);
      
      let productsData, totalCount;
      
      if (productData && typeof productData === 'object' && Array.isArray(productData.data)) {
        productsData = productData.data;
        totalCount = productData.total || productData.count || productsData.length;
      } else if (Array.isArray(productData)) {
        productsData = productData;
        totalCount = productData.length < pageSize ? skip + productData.length : skip + productData.length + 1;
      } else if (productData && typeof productData === 'object' && Array.isArray(productData.products)) {
        productsData = productData.products;
        totalCount = productData.total || productData.count || productsData.length;
      } else {
        productsData = [];
        totalCount = 0;
      }
      
      const transformedProducts = productsData.map(product => ({
        id: product.id,
        name: product.name,
        description: product.description,
        unitOfMeasure: product.unit_of_measure,
        status: 'Active',
        createdAt: product.created_at ? new Date(product.created_at).toLocaleDateString() : 'N/A'
      }));
      setProducts(transformedProducts);
      setTotalProducts(totalCount);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch products:', err);
      setError('Failed to load products');
      toast({
        title: "Error",
        description: "Failed to load products. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  };

  const fetchProductById = async (productId) => {
    try {
      const productData = await productService.getProductById(productId);
      const transformedProduct = {
        id: productData.id,
        name: productData.name,
        description: productData.description,
        unitOfMeasure: productData.unit_of_measure,
        status: 'Active',
        createdAt: productData.created_at ? new Date(productData.created_at).toLocaleDateString() : 'N/A'
      };
      setSelectedProductDetails(transformedProduct);
      return transformedProduct;
    } catch (err) {
      console.error('Failed to fetch product details:', err);
      toast({
        title: "Error",
        description: "Failed to load product details. Please try again.",
        variant: "destructive",
      });
      return null;
    }
  };

  const handleAddProduct = () => {
    setAddProductDialogOpen(true);
  };


  const handleEditProduct = async (productId) => {
    const productDetails = await fetchProductById(productId);
    if (productDetails) {
      setSelectedProductDetails(productDetails);
      setEditProductDialogOpen(true);
    }
  };

  const handleDeleteProduct = (productId) => {
    const product = products.find(p => p.id === productId);
    setSelectedProduct(product);
    setDeleteProductDialogOpen(true);
  };

  const handleCreateProduct = async (newProduct) => {
    try {
      const productData = {
        name: newProduct.name,
        description: newProduct.description,
        unit_of_measure: newProduct.unit_of_measure
      };
      
      const createdProduct = await productService.createProduct(productData);
      const transformedProduct = {
        id: createdProduct.id,
        name: createdProduct.name,
        description: createdProduct.description,
        unitOfMeasure: createdProduct.unit_of_measure,
        status: 'Active',
        createdAt: createdProduct.created_at ? new Date(createdProduct.created_at).toLocaleDateString() : 'N/A'
      };
      
      setProducts([transformedProduct, ...products]);
      setAddProductDialogOpen(false);
      toast({
        title: "Product Added",
        description: `${newProduct.name} has been successfully added.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to add product. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleUpdateProduct = async (updatedProduct) => {
    try {
      const productData = {
        name: updatedProduct.name,
        description: updatedProduct.description,
        unit_of_measure: updatedProduct.unitOfMeasure
      };
      
      const response = await productService.updateProduct(updatedProduct.id, productData);
      
      const transformedProduct = {
        id: response.id,
        name: response.name,
        description: response.description,
        unitOfMeasure: response.unit_of_measure,
        status: 'Active',
        createdAt: response.created_at ? new Date(response.created_at).toLocaleDateString() : 'N/A'
      };
      
      setProducts(products.map(product => product.id === updatedProduct.id ? transformedProduct : product));
      setEditProductDialogOpen(false);
      setSelectedProductDetails(null);
      toast({
        title: "Product Updated",
        description: `${transformedProduct.name} has been successfully updated.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to update product. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleConfirmDeleteProduct = async (productToDelete) => {
    try {
      const response = await productService.deleteProduct(productToDelete.id);
      setProducts(products.filter(product => product.id !== productToDelete.id));
      setDeleteProductDialogOpen(false);
      setSelectedProduct(null);
      toast({
        title: "Product Deleted",
        description: `${productToDelete.name} has been successfully deleted.`,
      });
    } catch (error) {
      console.error('Delete product error:', error);
      toast({
        title: "Error",
        description: `Failed to delete product: ${error.message || 'Please try again.'}`,
        variant: "destructive",
      });
    }
  };

  if (isInitialLoad) {
    return <FullScreenLoader isLoading={isInitialLoad} />;
  }

  if (error) {
    return <div className="flex justify-center items-center h-64 text-red-500">{error}</div>
  }
  
  const totalPages = Math.ceil(totalProducts / pageSize);

  return (
    <div>
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-foreground">Products Management</h1>
          <p className="text-xs text-muted-foreground">Define and catalog cargo commodities and transport items.</p>
        </div>
        <div className="flex items-center space-x-2">
          <CommonButton onClick={handleAddProduct} variant="default" icon={Plus}>
            Add Product
          </CommonButton>
        </div>
      </div>
      
      <ProductsManagementHeader products={products} />
      
      <TableBlurLoader 
        isLoading={loading} 
        className="mt-4"
      >
        <div className="border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform" style={{boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)'}}>
          <div className="border border-border py-4 rounded-[16px] bg-background">
            {products.length > 0 ? (
              <ProductsTable 
                products={products} 
                onEditProduct={handleEditProduct}
                onDeleteProduct={handleDeleteProduct}
                currentPage={currentPage}
                totalPages={totalPages}
                totalProducts={totalProducts}
                pageSize={pageSize}
                onPageChange={setPage}
              />
            ) : (
              <div className="flex justify-center items-center h-64 text-muted-foreground">
                {loading ? "" : "No data available"}
              </div>
            )}
          </div>
        </div>
      </TableBlurLoader>

      <AddProductDialog
        open={addProductDialogOpen}
        onOpenChange={setAddProductDialogOpen}
        onAddProduct={handleCreateProduct}
      />      
      <EditProductDialog
        open={editProductDialogOpen}
        onOpenChange={(open) => {
          setEditProductDialogOpen(open);
          if (!open) setSelectedProductDetails(null);
        }}
        product={selectedProductDetails}
        onUpdateProduct={handleUpdateProduct}
      />
      
      {selectedProduct && (
        <DeleteProductDialog
          open={deleteProductDialogOpen}
          onOpenChange={setDeleteProductDialogOpen}
          product={selectedProduct}
          onDeleteProduct={handleConfirmDeleteProduct}
        />
      )}
    </div>
  );
};

export default ProductsManagementPage;